import React, { useRef, useEffect, useState } from 'react';
import { Trash, WarningCircle } from 'phosphor-react';
import { Loader } from './Loader';
import { cn } from '../utils/cn';

interface DeleteButtonProps {
  onDelete: () => void | Promise<void>;
  loading?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  label?: string;
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  onDelete,
  loading = false,
  size = 'sm',
  className,
  label = 'Borrar',
}) => {
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!confirming) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current?.contains(e.target as Node)) return;
      setConfirming(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [confirming]);

  const iconSize = size === 'sm' ? 14 : 16;

  const handleTrashClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirming(true);
  };

  const handleConfirmClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setRunning(true);
      await onDelete();
    } finally {
      setRunning(false);
      setConfirming(false);
    }
  };

  const isLoading = loading || running;

  return (
    <div ref={wrapperRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        onClick={confirming ? handleConfirmClick : handleTrashClick}
        disabled={isLoading}
        aria-label={confirming ? label : 'Eliminar'}
        className={cn(
          'flex items-center justify-center rounded transition-colors disabled:opacity-50',
          size === 'sm' ? 'p-1' : 'px-1.5 py-1',
          confirming
            ? 'text-destructive hover:bg-destructive/20'
            : 'text-text-subtle hover:bg-destructive/10 hover:text-destructive',
        )}
      >
        {isLoading ? (
          <Loader />
        ) : confirming ? (
          <WarningCircle size={iconSize} weight="fill" />
        ) : (
          <Trash size={iconSize} weight="regular" />
        )}
      </button>

      {confirming && !isLoading && (
        <div className="fixed z-[9999] pointer-events-none"
          style={{
            top: wrapperRef.current
              ? wrapperRef.current.getBoundingClientRect().top - 28
              : 0,
            left: wrapperRef.current
              ? wrapperRef.current.getBoundingClientRect().left + wrapperRef.current.offsetWidth / 2
              : 0,
            transform: 'translateX(-50%)',
          }}
        >
          <span className="block bg-destructive text-white text-xs font-sans font-medium px-2 py-0.5 rounded whitespace-nowrap shadow">
            {label}
          </span>
        </div>
      )}
    </div>
  );
};
